import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Flame,
  Mail,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  ShieldAlert,
  UserCircle,
  Clock,
} from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import { authService } from '../services/authService';

function resolveFeedback(result, email) {
  if (result?.success) {
    return {
      kind: 'success',
      title: 'Lien envoyé',
      lines: [
        `Un email vient d’être envoyé à ${email.trim()}.`,
        'Le lien est valable 1 heure. Consultez aussi vos courriers indésirables.',
      ],
    };
  }

  const code = result?.code;
  const msg = (result?.message || '').toLowerCase();

  if (code === 'ADMIN_RESET_ONLY' || msg.includes('administrateur')) {
    return {
      kind: 'admin_only',
      title: 'Accès réservé aux administrateurs',
      lines: [
        `L’adresse ${email.trim()} n’est pas un compte administrateur PFE, ou n’est pas enregistrée.`,
        'Les comptes client changent leur mot de passe dans Mon Profil après connexion, ou via leur administrateur.',
      ],
    };
  }

  if (result?.status === 503 || msg.includes('email') || msg.includes('configuré')) {
    return {
      kind: 'service',
      title: 'Envoi temporairement indisponible',
      lines: [result?.message || 'Réessayez dans quelques minutes ou contactez le support PETROFEU.'],
    };
  }

  if (result?.status === 429 || msg.includes('trop') || msg.includes('bloqu')) {
    return {
      kind: 'rate_limit',
      title: 'Patience un instant',
      lines: [result?.message || 'Trop de demandes. Réessayez dans quelques minutes.'],
    };
  }

  return {
    kind: 'error',
    title: 'Impossible d’envoyer le lien',
    lines: [result?.message || 'Une erreur est survenue. Vérifiez l’email saisi et réessayez.'],
  };
}

function FeedbackPanel({ feedback, panelMeta, onRetry }) {
  const StatusIcon = panelMeta.icon;
  return (
    <>
      <div className="flex flex-col items-center text-center">
        <div
          className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full ring-4 ${panelMeta.iconWrap}`}
        >
          <StatusIcon className="h-7 w-7" strokeWidth={2} />
        </div>
        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{feedback.title}</h2>
        <div className="space-y-2 text-sm text-gray-600 leading-relaxed max-w-sm">
          {feedback.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-2">
        {feedback.kind === 'success' ? (
          <Link to="/login">
            <Button variant="primary" className="w-full">
              Retour à la connexion
            </Button>
          </Link>
        ) : feedback.kind === 'admin_only' ? (
          <>
            <Link to="/login">
              <Button variant="primary" className="w-full" icon={UserCircle}>
                Se connecter (Mon Profil)
              </Button>
            </Link>
            <Button variant="outline" className="w-full" onClick={onRetry}>
              Essayer un autre email
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" className="w-full" onClick={onRetry}>
              Réessayer
            </Button>
            <Link to="/login">
              <Button variant="outline" className="w-full" icon={ArrowLeft}>
                Retour à la connexion
              </Button>
            </Link>
          </>
        )}
      </div>
    </>
  );
}

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);
    setLoading(true);

    try {
      const result = await authService.resetPassword(email);
      setFeedback(resolveFeedback(result, email));
    } catch {
      setFeedback(
        resolveFeedback({ success: false, message: 'Connexion au serveur impossible.' }, email)
      );
    } finally {
      setLoading(false);
    }
  };

  const panelMeta = useMemo(() => {
    if (!feedback) return null;
    switch (feedback.kind) {
      case 'success':
        return {
          icon: CheckCircle,
          iconWrap: 'bg-red-50 text-red-600 ring-red-100',
          border: 'border-red-200',
          bg: 'bg-gradient-to-br from-red-50/80 to-white',
        };
      case 'admin_only':
        return {
          icon: ShieldAlert,
          iconWrap: 'bg-amber-50 text-amber-700 ring-amber-100',
          border: 'border-amber-200',
          bg: 'bg-gradient-to-br from-amber-50/60 to-white',
        };
      case 'rate_limit':
        return {
          icon: Clock,
          iconWrap: 'bg-orange-50 text-orange-600 ring-orange-100',
          border: 'border-orange-200',
          bg: 'bg-gradient-to-br from-orange-50/50 to-white',
        };
      default:
        return {
          icon: AlertCircle,
          iconWrap: 'bg-red-50 text-red-600 ring-red-100',
          border: 'border-red-200',
          bg: 'bg-gradient-to-br from-red-50/60 to-white',
        };
    }
  }, [feedback]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50" />

      <div className="relative flex-1 flex items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-6 md:mb-8">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 300 }}
              className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-red-600 mb-3 shadow-lg shadow-red-600/25"
            >
              <Flame className="w-8 h-8 md:w-9 md:h-9 text-white" />
            </motion.div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">PFE</h1>
            <p className="text-gray-600 text-xs md:text-sm mt-1">
              Gestion des Équipements de Sécurité Incendie
            </p>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
            <AnimatePresence mode="wait">
              {feedback && panelMeta ? (
                <motion.div
                  key={feedback.kind + feedback.title}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className={`rounded-xl border p-5 md:p-6 ${panelMeta.border} ${panelMeta.bg}`}
                >
                  <FeedbackPanel
                    feedback={feedback}
                    panelMeta={panelMeta}
                    onRetry={() => setFeedback(null)}
                  />
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      Mot de passe oublié
                    </h2>
                    <p className="text-sm text-gray-600">
                      Saisissez l’<span className="font-medium text-red-700">email administrateur</span>{' '}
                      enregistré sur la plateforme. Un lien sécurisé vous sera envoyé.
                    </p>
                  </div>

                  <div className="rounded-lg border border-red-100 bg-red-50/50 px-3 py-2.5 text-xs text-gray-600">
                    <span className="font-semibold text-red-800">Compte client ?</span> Utilisez Mon Profil
                    une fois connecté, ou demandez à votre administrateur PETROFEU.
                  </div>

                  <Input
                    label="Email administrateur"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    icon={Mail}
                    placeholder="admin@entreprise.com"
                    required
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                    loading={loading}
                    disabled={loading}
                  >
                    Envoyer le lien de réinitialisation
                  </Button>

                  <Link to="/login">
                    <Button variant="outline" className="w-full" icon={ArrowLeft}>
                      Retour à la connexion
                    </Button>
                  </Link>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
